import { PostType } from './_types'
import { CallType } from './call'
import { User } from './user'

export type ACTIVITY_PAGE_TYPE = 'important' | 'reaction' | 'footprint'

export const ACTIVITY_WHITE_LIST = [
  'like',
  'follow',
  'follow_request',
  'follow_accepted',
  'group_promote',
  'group_ownership',
  'group_accepted',
  'group_request',
  'group_created',
  'group_transfer_request',
  'group_invite',
  'group_deputization_accepted',
  'group_deputization_declined',
  'group_deputization_request',
  'group_transfer_accepted',
  'group_transfer_declined',
  'group_walkthrough_request',
  'post_group_tagged_all',
  'profile_screenshot',
  'reply',
  'repost',
  'review_pending',
  'review_gift',
  'conference_call_invite',
  'id_check_checking',
  'id_check_success',
  'id_check_failed',
  'today_news',
  'popular_post',
  'vip_game_reward',
  'user_birth_date',
  'post_move_to_thread',
  'tagged',
  'custom_announcement',
  'wallet_daily_quests_completed',
  'invitee_create_account',
  'follow_inviter',
  'followed_inviter',
  'gift_received_user',
  'gift_received_post',
  'gift_received_group',
  'gift_received_group_post',
] as const

export type ActivityType = typeof ACTIVITY_WHITE_LIST[number]

export type Activity<T extends ActivityType = ActivityType> = {
  type: T
} & (T extends ActivityWithDetail['type']
  ? ActivityWithDetail
  : ActivityWithOutDetail)

type ActivityTemplate<
  T extends ActivityType = ActivityType,
  P extends Object = {}
> = P & {
  type: T
  user: ActivityUser
  created_at: number
}

type ActivityWithOutDetail = ActivityTemplate<
  Exclude<ActivityType, ActivityWithDetail['type']>
>

type ActivityWithDetail =
  | ActivityTemplate<
      'like',
      {
        to_post: ActivityPost & {
          likers: ActivityUser[]
          likers_count: number
        }
      }
    >
  | ActivityTemplate<
      'follow',
      {
        followers: ActivityUser[]
        followers_count: number
      }
    >
  | ActivityTemplate<
      'conference_call_invite',
      {
        from_post: ActivityPost
        metadata: { bulk_invitation: boolean }
      }
    >
  | ActivityTemplate<'today_news' | 'popular_post', { from_post: ActivityPost }>
  | ActivityTemplate<
      | 'group_deputization_request'
      | 'group_deputization_accepted'
      | 'group_deputization_declined'
      | 'group_promote'
      | 'group_ownership'
      | 'group_invite'
      | 'group_created'
      | 'group_transfer_request'
      | 'group_transfer_declined'
      | 'group_transfer_accepted'
      | 'group_accepted'
      | 'group_request'
      | 'group_walkthrough_request',
      { group: ActivityGroup }
    >
  | ActivityTemplate<
      'reply' | 'repost' | 'post_move_to_thread' | 'post_group_tagged_all',
      {
        group?: ActivityGroup
        from_post: ActivityPost
        to_post?: ActivityPost
      }
    >
  | ActivityTemplate<'vip_game_reward', { vip_reward: number }>
  | ActivityTemplate<'tagged', { from_post: ActivityPost }>
  | ActivityTemplate<
      'review_pending' | 'review_gift',
      { metadata: { content_preview: string } }
    >
  | ActivityTemplate<
      'custom_announcement',
      {
        metadata: {
          title: string
          body: string
          url: string
        }
      }
    >
  | ActivityTemplate<
      'user_birth_date',
      {
        birthday_users: ActivityUser[]
        birthday_users_count: number
      }
    >
  | ActivityTemplate<
      'gift_received_user' | 'gift_received_post' | 'gift_received_group_post',
      { metadata: { anonymous: boolean } }
    >
  | ActivityTemplate<
      'gift_received_group',
      {
        metadata: {
          anonymous: boolean
          group_topic: string
        }
      }
    >

export type ActivityUser = Pick<
  User,
  'id' | 'nickname' | 'profile_icon_thumbnail'
>

export type ActivityGroup = {
  id: number
  topic: string
}

export type ActivityPost = {
  id: number
  thread_id?: number
  post_type?: PostType
  text?: string
  conference_call?: {
    id: number
    call_type?: CallType
  }
}

// // example usage

// const mockUser: ActivityUser = { id: 1, nickname: 'a', profile_icon: 'a' }

// // case 1: declared type
// const activity1: Activity<'vip_game_reward'> = {
//   type: 'vip_game_reward', // `type` is only allowed to be 'vip_game_reward'
//   user: mockUser,
//   created_at: 0,
//   vip_reward: 1, // this field will be hinted by typescript
// }

// // case 2: no declared type
// const activity2: Activity = {
//   type: 'vip_game_reward',
//   user: mockUser,
//   created_at: 0,
//   vip_reward: 1, // because `type` is 'vip_game_reward', this field will be hinted by typescript followed declaration in `ActivityWithDetail`
// }

// // case 3: no declared type
// const activity3: Activity = {
//   type: 'follow_request',
//   user: mockUser,
//   created_at: 0,
//   // typescript will be hinted no more property because no `type` 'follow_request' declaration in `ActivityWithDetail`
// }

// // case 4: use in switch case
// const x = (atv: Activity) => {
//   switch (atv.type) {
//     case 'vip_game_reward':
//       console.log(atv.vip_reward) // this field will be hinted by typescript
//   }
// }
